import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createRoot } from 'react-dom/client';

// Component imports
import DeliveryMethodSelector from './components/DeliveryMethodSelector.jsx';
import DesiredDateSelector from './components/desiredDateSelector/DesiredDateSelector.jsx';
import PickupLocationSelector from './components/PickupLocationSelector.jsx';
import InvoiceSelector from './components/InvoiceSelector.jsx';
import DeliveryUI from './components/DeliveryUI.jsx';
import Summary from './components/Summary.jsx';
import CheckoutButton from './components/CheckoutButton.jsx';

function App() {
	const gadgetApi = new Gadget({});
	const [settings, setSettings] = useState(null);
	const [cartAttributes, setCartAttributes] = useState({});
	const [lastCartAttributes, setLastCartAttributes] = useState({});
	const [cartData, setCartData] = useState(null);

	const hourRef = useRef(null);

	useEffect(() => {
		const mountNode = document.getElementById('french-custom-delivery-pickup-root');

		if (!mountNode) return;

		const settings = window.frenchCustomDeliveryPickupSettings || null;

		Object.keys(settings).forEach(function (key, index) {
			if (typeof settings[key] === 'string') {
				settings[key] = settings[key]
					.replaceAll('&#39;', "'")
					.replaceAll('&quot;', '"')
					.replaceAll('&amp;', '&')
					.replaceAll('&lt;', '<')
					.replaceAll('&gt;', '>')
					.replaceAll('&nbsp;', ' ')
					.replaceAll('&copy;', '©')
					.replaceAll('&reg;', '®')
					.replaceAll('&trade;', '™');
			}
		});

		setSettings(settings);

		let onLoadCartAttributes = { deliveryMethod: 'pickup', invoiceType: 'pf' };

		try {
			onLoadCartAttributes = settings?.cartAttributes || { deliveryMethod: 'pickup' };
		} catch (error) {
			console.error('Failed to parse cart attributes:', error);
			onLoadCartAttributes = { deliveryMethod: 'pickup' };
		}

		setLastCartAttributes(onLoadCartAttributes);

		// setCartAttributes({
		//   ...onLoadCartAttributes,
		//   deliveryMethod: onLoadCartAttributes.deliveryMethod || 'pickup',
		//   invoiceType: onLoadCartAttributes.invoiceType || 'pf'
		// });
	}, []);

	useEffect(() => {
		// console.log('cartAttributes', cartAttributes);
		if (cartAttributes && Object.keys(cartAttributes).length > 0) {
			// Compare current cart attributes with last ones to avoid unnecessary updates
			const currentAttributesString = JSON.stringify(cartAttributes);
			const lastAttributesString = JSON.stringify(lastCartAttributes);

			if (currentAttributesString !== lastAttributesString) {
				updateCartAttributes(cartAttributes);
				setLastCartAttributes(cartAttributes);
			}
		}
	}, [cartAttributes, lastCartAttributes]);

	useEffect(() => {
		const fetchCartData = async () => {
			try {
				const response = await fetch('/cart.js');
				const data = await response.json();

				if (!data) return;

				const token = data.token.split('?')[0];
				const containsPhysicalProducts = data.requires_shipping;
				const cartAttributesFromCart = data.attributes || {};

				setCartData(data);
				setSettings((prevSettings) => ({
					...prevSettings,
					cartToken: token,
				}));

				const initialAttributes = {
					...cartAttributesFromCart,
					cartToken: token,
				};

				// -------------------------------
				// VALIDATE DESIRED DATE
				// -------------------------------
				if (initialAttributes.desiredDate) {
					const parseDateFromCart = (dateString) => {
						const [day, month, year] = dateString.split('-').map(Number);
						const date = new Date(year, month - 1, day);
						date.setHours(0, 0, 0, 0);
						return date;
					};

					const formatDateForCart = (date) => {
						const day = date.getDate().toString().padStart(2, '0');
						const month = (date.getMonth() + 1).toString().padStart(2, '0');
						const year = date.getFullYear();
						return `${day}-${month}-${year}`;
					};

					const tomorrow = new Date();
					tomorrow.setDate(tomorrow.getDate() + 1);
					tomorrow.setHours(0, 0, 0, 0);

					const existingDate = parseDateFromCart(initialAttributes.desiredDate);

					if (existingDate < tomorrow) {
						initialAttributes.desiredDate = formatDateForCart(tomorrow);
					}
				}

				// -------------------------------
				// FIXED DELIVERY METHOD LOGIC
				// -------------------------------
				const VALID_METHODS = ['pickup', 'delivery', 'digitalProductsOnly'];

				const rawMethod = (cartAttributesFromCart.deliveryMethod || '').trim();
				const isValid = VALID_METHODS.includes(rawMethod);

				if (!containsPhysicalProducts) {
					initialAttributes.deliveryMethod = 'digitalProductsOnly';
				} else if (!isValid) {
					initialAttributes.deliveryMethod = 'pickup';
				} else {
					initialAttributes.deliveryMethod = rawMethod;
				}

				// -------------------------------
				// INVOICE TYPE LOGIC
				// -------------------------------
				const hasInvoiceType =
					cartAttributesFromCart.invoiceType && cartAttributesFromCart.invoiceType.trim() !== '';

				if (hasInvoiceType) {
					initialAttributes.invoiceType = cartAttributesFromCart.invoiceType;
				} else {
					// Default to 'pf' if no invoice type is set
					initialAttributes.invoiceType = 'pf';
				}

				//console.log('Invoice type set to:', initialAttributes.invoiceType);

				setCartAttributes(initialAttributes);
				setLastCartAttributes(initialAttributes);
			} catch (error) {
				console.error('Failed to fetch cart data:', error);
			}
		};

		fetchCartData();
	}, []);

	const updateCartAttributes = async (attributes) => {
		try {
			const response = await fetch('/cart/update.json', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json',
					'X-Requested-With': 'XMLHttpRequest',
				},
				body: JSON.stringify({
					attributes: attributes,
				}),
			});

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const data = await response.json();

			return data;
		} catch (error) {
			console.error('Failed to update cart attributes:', error);
			// Optionally show user-friendly error message
			return null;
		}
	};

	if (!settings || !cartAttributes) return null;

	if (cartData && cartData.item_count === 0) {
		return null;
	}

	return (
		<div className="french-shipping">
			<DeliveryMethodSelector
				settings={settings}
				cartData={cartData}
				cartAttributes={cartAttributes}
				setCartAttributes={setCartAttributes}
			/>
			{cartAttributes.deliveryMethod !== 'digitalProductsOnly' && (
				<>
					<DesiredDateSelector
						gadgetApi={gadgetApi}
						settings={settings}
						cartAttributes={cartAttributes}
						setCartAttributes={setCartAttributes}
						hourRef={hourRef}
					/>

					{cartAttributes.deliveryMethod == 'pickup' ? (
						<PickupLocationSelector
							settings={settings}
							cartAttributes={cartAttributes}
							setCartAttributes={setCartAttributes}
						/>
					) : (
						<DeliveryUI settings={settings} cartAttributes={cartAttributes} />
					)}
				</>
			)}
			<InvoiceSelector
				gadgetApi={gadgetApi}
				settings={settings}
				cartAttributes={cartAttributes}
				setCartAttributes={setCartAttributes}
			/>

			<Summary settings={settings} cartAttributes={cartAttributes} />

			<CheckoutButton
				gadgetApi={gadgetApi}
				settings={settings}
				cartAttributes={cartAttributes}
				hourRef={hourRef}
				setCartAttributes={setCartAttributes}
			/>
		</div>
	);
}

function mount() {
	const el = document.getElementById('french-custom-delivery-pickup-root');
	if (!el) return;
	const root = createRoot(el);
	root.render(<App />);
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', mount);
} else {
	mount();
}
